import type { ComponentProps, ReactNode, Ref } from "react";
import type { Account } from "@common/types/domains/accounting/account";
import { AccountCombobox } from "@components/accounts/AccountCombobox";
import { FormikField } from "@components/forms/FormikField/FormikField";
import { ariaLabelledbyProps } from "@design-system/formControl/ariaLabelledbyProps";
import type { TooltipTriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import { Picker } from "@design-system/Picker/Picker.lib";
import { useAccounts } from "@hooks/useAccounts";
import type { FieldPath, FieldPathEmptyValue } from "./fieldPath.types";
import { pickChromeProps } from "./kitChromeProps";
import { type KitClearableProps, toClearTarget } from "./kitClearable";
import type {
    KitFieldChromeProps,
    KitFieldControlProps,
} from "./kitFieldProps.types";

type LedgerAccountFieldValue = string | null | undefined;

/** Paths of `Values` that hold one ledger (chart of accounts) account id. */
export type LedgerAccountFieldPath<Values> = FieldPath<
    Values,
    LedgerAccountFieldValue,
    string
>;

type AccountComboboxOptions = Pick<
    ComponentProps<typeof AccountCombobox>,
    | "entityId"
    | "showAccountCode"
    | "allowedAccountTypes"
    | "suggestedType"
    | "onCreateAccount"
>;

interface LedgerAccountControlOptions extends AccountComboboxOptions {
    placeholder?: string;
    buttonProps?: TooltipTriggerButtonProps;
}

export type LedgerAccountFieldProps<Values, Name extends string> = Omit<
    KitFieldControlProps<
        Name,
        HTMLButtonElement,
        string | FieldPathEmptyValue<Values, Name>
    >,
    "required"
> &
    KitFieldChromeProps &
    KitClearableProps<FieldPathEmptyValue<Values, Name>> &
    LedgerAccountControlOptions;

interface LedgerAccountFieldControlProps extends LedgerAccountControlOptions {
    "aria-labelledby"?: string;
    accountId: LedgerAccountFieldValue;
    isClearable: boolean;
    onAccountChange: (account: Account | null) => void;
    onBlur: () => void;
    ref?: Ref<HTMLButtonElement>;
    invalid: boolean;
    disabled?: boolean;
}

const LedgerAccountFieldControl = ({
    "aria-labelledby": ariaLabelledby,
    accountId,
    isClearable,
    onAccountChange,
    onBlur,
    ref,
    invalid,
    disabled,
    entityId,
    showAccountCode = false,
    allowedAccountTypes,
    suggestedType,
    onCreateAccount,
    placeholder = "Select account...",
    buttonProps,
}: LedgerAccountFieldControlProps) => {
    const { accountsById } = useAccounts({ entityId });
    const selectedAccount: Account | undefined = accountId
        ? accountsById[accountId]
        : undefined;
    const {
        tooltip,
        variant = "primary",
        ...buttonRest
    } = { disabled, ...buttonProps };

    return (
        <AccountCombobox
            entityId={entityId}
            selectedAccount={selectedAccount}
            onChange={onAccountChange}
            disabled={disabled}
            showAccountCode={showAccountCode}
            allowedAccountTypes={allowedAccountTypes}
            suggestedType={suggestedType}
            onCreateAccount={onCreateAccount}
            canClear={isClearable}
            onBlur={onBlur}
            invalid={invalid}
            trigger={
                <Picker.ButtonTrigger
                    ref={ref}
                    render={
                        <Picker.Button
                            {...buttonRest}
                            {...ariaLabelledbyProps(ariaLabelledby)}
                            variant={variant}
                            size={buttonRest.size ?? "md"}
                        />
                    }
                    placeholder={placeholder}
                    tooltip={tooltip}
                />
            }
        />
    );
};

/**
 * Chart of accounts picker bound to a typed Formik path. The form stores the
 * account id; the field resolves the `Account` for `entityId` through
 * `useAccounts().accountsById`. Offers "Clear selection" only when
 * `clearable` comes with an `emptyValue` the path allows.
 */
export const LedgerAccountField = <
    Values,
    Name extends LedgerAccountFieldPath<Values> =
        LedgerAccountFieldPath<Values>,
>(
    props: LedgerAccountFieldProps<Values, Name>,
): ReactNode => {
    const {
        name,
        ref,
        disabled,
        onValueChange,
        entityId,
        showAccountCode,
        allowedAccountTypes,
        suggestedType,
        onCreateAccount,
        placeholder,
        buttonProps,
    } = props;
    const clearTarget = toClearTarget(props);

    return (
        <FormikField<LedgerAccountFieldValue>
            name={name}
            control="button"
            {...pickChromeProps(props)}
        >
            {({ field, helpers, invalid }) => (
                <LedgerAccountFieldControl
                    aria-labelledby={props["aria-labelledby"]}
                    accountId={field.value}
                    isClearable={clearTarget !== undefined}
                    onAccountChange={(account) => {
                        if (account) {
                            void helpers.setValue(account.id);
                            onValueChange?.(account.id);
                        } else if (clearTarget) {
                            void helpers.setValue(clearTarget.emptyValue);
                            onValueChange?.(clearTarget.emptyValue);
                        }
                    }}
                    onBlur={() => void helpers.setTouched(true)}
                    ref={ref}
                    invalid={invalid}
                    disabled={disabled}
                    entityId={entityId}
                    showAccountCode={showAccountCode}
                    allowedAccountTypes={allowedAccountTypes}
                    suggestedType={suggestedType}
                    onCreateAccount={onCreateAccount}
                    placeholder={placeholder}
                    buttonProps={buttonProps}
                />
            )}
        </FormikField>
    );
};
